/**
 * Lazy-load decorative / below-fold background videos.
 * - data-wod3d-lazy: activate near viewport (IntersectionObserver)
 * - data-wod3d-defer-idle: activate after window load + idle (hero LCP path)
 */
(function () {
  var SELECTOR = 'video[data-wod3d-lazy]';
  var DEFER_SELECTOR = 'video[data-wod3d-defer-idle]';

  function prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  function revealHeroVideo(video) {
    video.classList.add('is-visible');
    var media = video.closest('.wod3d-hero__media, .wod3d-hero__photo');
    if (media) media.classList.add('wod3d-hero__media--video-ready');
  }

  function activate(video) {
    if (!video || video.dataset.wod3dLazyLoaded === '1') return;

    if (prefersReducedMotion() && video.hasAttribute('data-wod3d-defer-idle')) {
      video.dataset.wod3dLazyLoaded = '1';
      return;
    }

    video.dataset.wod3dLazyLoaded = '1';

    var dataSrc = video.getAttribute('data-src');
    if (dataSrc) {
      video.src = dataSrc;
      video.removeAttribute('data-src');
    }

    video.querySelectorAll('source[data-src]').forEach(function (source) {
      source.src = source.getAttribute('data-src');
      source.removeAttribute('data-src');
    });

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.removeAttribute('controls');
    video.controls = false;

    function onReady() {
      revealHeroVideo(video);
      tryPlay();
    }

    function tryPlay() {
      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {});
      }
    }

    video.addEventListener('loadeddata', onReady, { once: true });
    video.addEventListener('canplay', onReady, { once: true });

    try {
      video.load();
    } catch (e) {}

    tryPlay();

    try {
      document.dispatchEvent(
        new CustomEvent('wod3d:lazy-video-ready', { detail: { video: video } })
      );
    } catch (e) {}
  }

  function scheduleIdle(fn) {
    var run = function () {
      try {
        fn();
      } catch (e) {}
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 2500 });
    } else {
      window.setTimeout(run, 800);
    }
  }

  /**
   * Hero / above-the-fold videos: keep HLS/MP4 off the critical path.
   * Wait for window load, a short settle delay (so LCP can attribute the poster
   * image), then idle callback before assigning source URLs.
   */
  function afterCriticalPath(fn) {
    var settleMs = 2500;
    var start = function () {
      window.setTimeout(function () {
        scheduleIdle(fn);
      }, settleMs);
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  function initDeferIdle(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var videos = scope.querySelectorAll(DEFER_SELECTOR);
    if (!videos.length) return;

    afterCriticalPath(function () {
      videos.forEach(activate);
    });
  }

  function initNearViewport(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var videos = Array.prototype.slice.call(scope.querySelectorAll(SELECTOR)).filter(function (video) {
      // Hero / LCP path: do not let IntersectionObserver pull HLS early.
      return !video.hasAttribute('data-wod3d-defer-idle');
    });
    if (!videos.length) return;

    if (!('IntersectionObserver' in window)) {
      videos.forEach(activate);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          activate(entry.target);
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '300px 0px', threshold: 0.01 }
    );

    videos.forEach(function (video) {
      if (video.dataset.wod3dLazyLoaded === '1') return;
      io.observe(video);
    });
  }

  function init(root) {
    initNearViewport(root);
    initDeferIdle(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();
